import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Globe, Zap, HeartHandshake, QrCode, MessageSquareText,
  ArrowRight, Lock, ScanLine, Rocket,
  Car, Dog, Luggage, KeyRound, Lightbulb, AlertTriangle,
  CheckCircle2, DollarSign, Users,
  Check, X,
  Backpack, PawPrint, Droplets, Palette, Sparkles, Plane,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { BrandLogo } from "@/components/ui/brand-logo";
import { Footer } from "@/components/Footer";
import { BackToTop } from "@/components/BackToTop";

const INDIEGOGO_URL = "https://www.indiegogo.com/en/projects/neetu-goyal/reachmenow";
const goIndiegogo = () => window.open(INDIEGOGO_URL, "_blank", "noopener,noreferrer");

const comparison = [
  { feature: "Number visibility", phone: "Exposed to everyone who sees it", rmn: "Never revealed" },
  { feature: "Spam exposure", phone: "High — robocalls and spam texts", rmn: "No number to leak" },
  { feature: "Privacy", phone: "Your identity is out there", rmn: "Your number is never displayed" },
  { feature: "Update flexibility", phone: "Reprint everything if it changes", rmn: "Update anytime — the tag stays the same" },
  { feature: "Contact methods", phone: "Calls and SMS only", rmn: "Real-time private messaging" },
  { feature: "Message history", phone: "Scattered across apps", rmn: "Organized in one dashboard" },
];

const tags = [
  { src: "https://customer-assets-eiarnc6j.emergentagent.net/wingman/52d859db-bf9d-41d8-b1f2-39f4d4c8e0cc/attachments/201812afad83486dab333644d983f314_ChatGPT%20Image%20Aug%2030%2C%202026%2C%2006_55_31%20PM.png", label: "Car Tag" },
  { src: "https://static.prod-images.emergentagent.com/jobs/28d8943f-c9ac-485c-b9e7-e08b7fa12f97/images/d362f792e73e6fbe78e76fe84fece65e63e3437da6b1f355e31fade23a4f6a19.jpeg", label: "Keychain" },
  { src: "https://customer-assets-wrfwihn1.emergentagent.net/job_qr-privacy-2/artifacts/k6oigei3_image.png", label: "Pet Tag" },
  { src: "https://customer-assets-wrfwihn1.emergentagent.net/job_qr-privacy-2/artifacts/blk943xs_image.png", label: "Luggage Tag" },
];

const useCases = [
  { icon: Car, title: "Car", desc: "Car, motorcycle, bicycle & more", img: "https://customer-assets-eiarnc6j.emergentagent.net/wingman/52d859db-bf9d-41d8-b1f2-39f4d4c8e0cc/attachments/53b2d8dfbc5043dc8c336309de7f257e_ChatGPT%20Image%20Aug%2031%2C%202026%2C%2008_45_16%20PM.png" },
  { icon: PawPrint, title: "Pet", desc: "Dogs, cats & other pets", img: "https://static.prod-images.emergentagent.com/jobs/28d8943f-c9ac-485c-b9e7-e08b7fa12f97/images/6d4d47688352173fa2536c82a84f8d4cf60d31403cf0c04ffc1b7032934b6098.jpeg" },
  { icon: Luggage, title: "Luggage", desc: "Suitcases, bags, backpacks", img: "https://static.prod-images.emergentagent.com/jobs/28d8943f-c9ac-485c-b9e7-e08b7fa12f97/images/c180e658a025126ac79f936c6d4b4fb8d56452bd41079f7bf2abe5d5ae999569.jpeg" },
  { icon: KeyRound, title: "Keys", desc: "House, office, garage keys", img: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxob3VzZSUyMGtleXMlMjBrZXljaGFpbnxlbnwwfHx8fDE3ODU2OTgzMDB8MA&ixlib=rb-4.1.0&w=800&q=75" },
  { icon: Plane, title: "Sports & Travel", desc: "Golf bags, gym gear & travel cases", img: "https://customer-assets-eiarnc6j.emergentagent.net/wingman/52d859db-bf9d-41d8-b1f2-39f4d4c8e0cc/attachments/0fdce69afb24481c947ce53b115a7731_ChatGPT%20Image%20Aug%2031%2C%202026%2C%2008_40_17%20PM.png" },
  { icon: Backpack, title: "Kids & Baby Gear", desc: "School bags, strollers & prams", img: "https://customer-assets-eiarnc6j.emergentagent.net/wingman/52d859db-bf9d-41d8-b1f2-39f4d4c8e0cc/attachments/14d632ea9095415087ee42d91e8deffa_ChatGPT%20Image%20Aug%2031%2C%202026%2C%2008_36_06%20PM.png" },
];

const wristbands = [
  { color: "Pink", url: "https://static.prod-images.emergentagent.com/jobs/28d8943f-c9ac-485c-b9e7-e08b7fa12f97/images/9ae731513575386dabdedf1e94b9cb9930c590712e784545428f12329057d249.jpeg" },
  { color: "Blue", url: "https://static.prod-images.emergentagent.com/jobs/28d8943f-c9ac-485c-b9e7-e08b7fa12f97/images/20999c7309bcfb095939ea78d815e5c27305eda8330ee1d61d25908168d3873b.jpeg" },
  { color: "Black", url: "https://static.prod-images.emergentagent.com/jobs/28d8943f-c9ac-485c-b9e7-e08b7fa12f97/images/20d053b4d588292e5ab74aba55a6dfbd4449fbc574fbffaab6fa6d30f932db59.jpeg" },
  { color: "Purple", url: "https://static.prod-images.emergentagent.com/jobs/28d8943f-c9ac-485c-b9e7-e08b7fa12f97/images/d443be7ac8a8eb7eb4bfc894a391faef6289ac52be4ef0792960c3ac12ab087f.jpeg" },
  { color: "Orange", url: "https://static.prod-images.emergentagent.com/jobs/28d8943f-c9ac-485c-b9e7-e08b7fa12f97/images/2856ed9abecf0a936c95bfeee9390dc4966d3eb67e6dd8c38c07e662502fe47a.jpeg" },
];

const bandFeatures = [
  { icon: Droplets, text: "Waterproof & Sweatproof" },
  { icon: Sparkles, text: "Adjustable & Comfortable" },
  { icon: ScanLine, text: "Scan to Reach Privately" },
  { icon: Palette, text: "Multiple Colors" },
  { icon: ShieldCheck, text: "Safe for Kids & Seniors" },
];

const perfectFor = [
  { text: "Kids", dot: "bg-pink-400" },
  { text: "Senior Citizens", dot: "bg-brand-blue" },
  { text: "Families", dot: "bg-slate-800" },
  { text: "Caregivers", dot: "bg-purple-500" },
  { text: "Peace of Mind", dot: "bg-brand-orange" },
];

const tagStyles = [
  { label: "Rectangle", url: "https://static.prod-images.emergentagent.com/jobs/28d8943f-c9ac-485c-b9e7-e08b7fa12f97/images/c3f69180a3bf7c7cb4f00aa8b8a791846ae66e27f14645f0e2710c55079854eb.jpeg" },
  { label: "Mini Rectangle", url: "https://static.prod-images.emergentagent.com/jobs/28d8943f-c9ac-485c-b9e7-e08b7fa12f97/images/3cacfdc9190c1577434a1aad343c0e96b6fee63347f539b3b8ec87e9ef9e619d.jpeg" },
  { label: "Round", url: "https://static.prod-images.emergentagent.com/jobs/28d8943f-c9ac-485c-b9e7-e08b7fa12f97/images/14f295c190fc99e7b870976dc54482bbc6b41ef3375ea5f35f633b81018ef10c.jpeg" },
  { label: "Mini Round", url: "https://static.prod-images.emergentagent.com/jobs/28d8943f-c9ac-485c-b9e7-e08b7fa12f97/images/5eea58f08103ffa78d52f3d367e01eb3788c1333bdeed7544c5f9f763dc6059d.jpeg" },
  { label: "Bone (Pet)", url: "https://static.prod-images.emergentagent.com/jobs/28d8943f-c9ac-485c-b9e7-e08b7fa12f97/images/5e1e2633e8ed104b017d4c9d32883e896c7a2b7291f8bc276686fbbf14211082.jpeg" },
  { label: "Heart (Pet)", url: "https://static.prod-images.emergentagent.com/jobs/28d8943f-c9ac-485c-b9e7-e08b7fa12f97/images/224eb96a2139970ae60d91bdd7883ba560ac0939fc72391dcf8aac30625ba114.jpeg" },
  { label: "Silicone Loop Tag", url: "https://static.prod-images.emergentagent.com/jobs/28d8943f-c9ac-485c-b9e7-e08b7fa12f97/images/7c0751a85df2ddc01bb8de34779b2a76b49fb4f87adac4d2ebfe57f78cc90124.jpeg" },
  { label: "Clip-On Tag", url: "https://static.prod-images.emergentagent.com/jobs/28d8943f-c9ac-485c-b9e7-e08b7fa12f97/images/4e0854c4daaedc3c1399306a6fd5a22b9bc359a8c85c1c4c582ca1e8705558e4.jpeg" },
];

const TagShowcase = () => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setActive((a) => (a + 1) % tags.length), 3000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative w-[300px] sm:w-[360px]" data-testid="hero-tag-showcase">
      <div className="absolute -inset-8 bg-gradient-to-br from-blue-200/50 to-orange-200/50 blur-3xl rounded-full" />

      {/* Main rotating image */}
      <div className="relative z-10 bg-white rounded-3xl border border-slate-200 shadow-2xl p-6 h-[360px] flex items-center justify-center overflow-hidden">
        {tags.map((tag, i) => (
          <img
            key={tag.label}
            src={tag.src}
            alt={`ReachMeNow ${tag.label}`}
            width="1024"
            height="1024"
            decoding="async"
            className="absolute inset-0 m-auto max-h-[300px] max-w-[80%] object-contain transition-opacity duration-700 ease-in-out"
            style={{ opacity: active === i ? 1 : 0 }}
            data-testid={`hero-tag-main-${i}`}
          />
        ))}
        <div className="absolute top-4 left-4 z-20 bg-white rounded-full shadow-md border border-slate-100 px-3 py-1.5 flex items-center gap-1.5 text-xs font-semibold">
          <Lock className="h-3.5 w-3.5 text-brand-blue" /> {tags[active].label}
        </div>
      </div>
      <p className="relative z-10 mt-3 text-[11px] italic text-slate-400 text-center">Design render — final materials may vary.</p>

      {/* Thumbnails */}
      <div className="relative z-10 mt-5 flex items-center justify-center gap-3" data-testid="hero-tag-thumbs">
        {tags.map((tag, i) => (
          <button
            key={tag.label}
            onClick={() => setActive(i)}
            aria-label={tag.label}
            className={`h-16 w-16 rounded-2xl bg-white border-2 p-1.5 shadow-sm transition-all duration-200 hover:-translate-y-1 ${active === i ? "border-brand-blue ring-2 ring-brand-blue/20 -translate-y-1" : "border-slate-200"}`}
            data-testid={`hero-tag-thumb-${i}`}
          >
            <img src={tag.src} alt={tag.label} width="64" height="64" decoding="async" className="h-full w-full object-contain" />
          </button>
        ))}
      </div>
    </div>
  );
};

const benefits = [
  { icon: ShieldCheck, title: "Privacy First", desc: "Your phone number and email stay hidden. Always. Share a QR, not your identity.", color: "text-brand-blue", bg: "bg-blue-50" },
  { icon: Globe, title: "Works Anywhere", desc: "Visitors just scan and message from any phone camera — instantly, from anywhere.", color: "text-brand-orange", bg: "bg-orange-50" },
  { icon: Zap, title: "Real-Time Messaging", desc: "Chat live with whoever reaches out. Instant, secure, and delightfully simple.", color: "text-brand-blue", bg: "bg-blue-50" },
  { icon: HeartHandshake, title: "Peace of Mind", desc: "Stay reachable without being exposed. For you and the people you care about.", color: "text-brand-orange", bg: "bg-orange-50" },
];

const steps = [
  { icon: QrCode, title: "Get your ReachMeNow tag", desc: "Your personal ReachMeNow tag is ready to share anywhere — so people can reach you without ever knowing your number." },
  { icon: ScanLine, title: "Someone scans it", desc: "They land on a clean private page that says they're reaching out to you — no contact info revealed." },
  { icon: MessageSquareText, title: "Chat in real time", desc: "Messages will arrive on your app or dashboard instantly. Reply, and both sides stay in sync live." },
  { icon: ShieldCheck, title: "Stay private, always", desc: "Your number and email are never shared. Update your info anytime — the tag stays the same." },
];

const scenarios = [
  { icon: Lightbulb, title: "Your car headlights are on", color: "text-brand-orange", bg: "bg-orange-50" },
  { icon: AlertTriangle, title: "Your parked car just got hit", color: "text-brand-blue", bg: "bg-blue-50" },
  { icon: Dog, title: "Your pet was found nearby", color: "text-brand-orange", bg: "bg-orange-50" },
  { icon: Luggage, title: "Your lost luggage turned up", color: "text-brand-blue", bg: "bg-blue-50" },
  { icon: KeyRound, title: "Your keys were found", color: "text-brand-orange", bg: "bg-orange-50" },
];

const faqs = [
  { q: "Does the person scanning my tag need an app?", a: "No. Their phone camera opens a private message page in the browser. No app, no signup, no account." },
  { q: "Will people see my phone number?", a: "Never. That's the whole point. Visitors only ever see your first name and a message box — your number and email stay private." },
  { q: "When is ReachMeNow launching?", a: "Our campaign is now live on Indiegogo. Back us and we'll notify you the moment tags ship." },
  { q: "Is this really free to try?", a: "Yes. The demo is completely free — sign up, generate a QR, and experience the full flow today." },
  { q: "Where do you ship, and how much does it cost?", a: "We currently ship to Canada and the United States. Shipping is free for all campaign backers — no surprises at checkout. Physical tags are estimated to ship in February 2027, and every backer gets tracking by email the day their order leaves our facility." },
  { q: "What happens if the campaign doesn't reach its goal?", a: "This is a flexible funding campaign, which means we keep what we raise and every perk still gets fulfilled. Our goal reflects a full first production run. If we come in under it, we scale the run down rather than cancel it — we produce fewer tag styles, in smaller quantities, and fulfill in the order backers came in. That may mean a longer timeline or a narrower set of styles than shown here, and if so you'll hear it from us in an update within 15 days of the campaign closing, with the specifics and your options at that point. The ReachMeNow platform stays online and free to use regardless of how the campaign closes." },
  { q: "Do I need an app to receive messages?", a: "No. Sign in at reachmenow.ca from any phone, tablet, or computer and your messages are on your dashboard. iOS and Android apps with push notifications are what this campaign funds, and every backer gets them free, for life, when they launch." },
  { q: "Can someone abuse this to spam or harass me?", a: "You stay in control. Every conversation can be blocked or muted with one tap, and scan rates are limited to stop automated abuse. We never share your location, name, or contact details with the person scanning. If someone misuses a tag, you can disable it instantly — and if you need a replacement physical tag, just contact us at support@reachmenow.ca and we'll sort it out." },
];

const PRICING_URL = "https://www.indiegogo.com/en/projects/neetu-goyal/reachmenow";
const pricing = [
  {
    name: "Thank You Supporter", price: "C$10", original: "", label: "", popular: false,
    bullets: ["Digital Founding Supporter certificate", "Exclusive campaign updates", "Early access to future products and features"],
  },
  {
    name: "Early Bird", price: "C$25", original: "C$35", label: "Indiegogo Early Bird Price", popular: true,
    bullets: ["2 weatherproof Smart QR Car Stickers OR 1 Smart QR Car Sticker and 1 Smart Pet Tag OR 2 Smart Luggage Tags", "Anyone can contact you without seeing your number", "Lifetime private messaging", "Early Bird pricing"],
  },
  {
    name: "Founder's Bundle", price: "C$65", original: "C$100", label: "Indiegogo Early Bird Price", popular: false,
    bullets: ["2 Smart QR Car Stickers", "2 Smart Pet Tags", "2 Smart Luggage Tags", "Lifetime private messaging", "Founder badge"],
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-[#fafafa] text-slate-900 font-body pb-20 md:pb-0">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-brand-navy/95 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Back to top" className="bg-transparent cursor-pointer" data-testid="nav-logo-btn">
            <BrandLogo dark />
          </button>
          <div className="flex items-center gap-6">
            <button type="button" className="inline-flex items-center text-sm font-medium text-slate-200 hover:text-white transition-colors bg-transparent" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })} data-testid="nav-pricing-btn">
              Pricing
            </button>
            <Button className="hidden md:inline-flex bg-brand-orange hover:bg-brand-orangehover text-white rounded-full px-5" onClick={goIndiegogo} data-testid="nav-cta-btn">
              PreOrder on Indiegogo
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-blue-200/40 blur-3xl" />
        <div className="absolute top-40 -left-40 h-96 w-96 rounded-full bg-orange-200/40 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-5 py-12 md:py-28 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange bg-orange-50 px-3 py-1.5 rounded-full">
              <Lock className="h-3.5 w-3.5" /> Privacy-first contact
            </span>
            <h1 className="mt-6 font-heading font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Stay <span className="text-brand-blue">Reachable.</span><br /> Stay <span className="text-brand-orange">Private.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-lg leading-relaxed">
              Anyone can reach you without ever seeing your phone number or email address.
            </p>
            <p className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-brand-orange" data-testid="hero-indiegogo-note">
              <Rocket className="h-4 w-4" /> NOW LIVE ON INDIEGOGO — Early Bird pricing ends when the campaign closes.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button size="lg" className="bg-brand-blue hover:bg-brand-bluehover text-white rounded-full px-7 h-13 text-base shadow-lg shadow-blue-500/20 transition-all hover:-translate-y-0.5" onClick={() => navigate("/demo")} data-testid="hero-try-demo-btn">
                Generate Your Free Demo QR <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" className="rounded-full px-7 h-13 text-base border-brand-orange text-brand-orange hover:bg-orange-50 transition-all hover:-translate-y-0.5" onClick={goIndiegogo} data-testid="hero-waitlist-btn">
                <Rocket className="mr-1.5 h-4 w-4" /> PreOrder on Indiegogo
              </Button>
            </div>
          </motion.div>

          {/* Sticker product showcase */}
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="flex justify-center">
            <TagShowcase />
          </motion.div>
        </div>
      </section>

      {/* Problem / Solution */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-5 py-12 md:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange">The problem &amp; the fix</span>
            <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl tracking-tight">Reachable, without being exposed</h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              People want to reach you about your car, pet, luggage or keys — but sharing your phone number invites spam and exposes your privacy.
              ReachMeNow gives you a scannable QR tag so anyone can message you instantly, while your number and email stay completely private.
            </p>
          </div>
          <div className="mt-8 md:mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {scenarios.map((s, i) => (
              <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.07 }}
                className="bg-[#fafafa] border border-slate-200 rounded-2xl p-6 text-center transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:bg-white" data-testid={`scenario-card-${i}`}>
                <div className={`h-12 w-12 rounded-xl ${s.bg} flex items-center justify-center mx-auto`}>
                  <s.icon className={`h-6 w-6 ${s.color}`} strokeWidth={2.2} />
                </div>
                <p className="mt-4 font-heading font-semibold text-sm leading-snug">{s.title}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-5 py-12 md:py-20">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange">How it works</span>
          <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl tracking-tight">Four steps to private contact</h2>
        </div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {steps.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}
              className="relative" data-testid={`how-step-${i}`}>
              <div className="flex items-center gap-4">
                <div className="relative h-14 w-14 rounded-2xl bg-gradient-to-br from-brand-blue to-brand-orange flex items-center justify-center text-white shadow-lg shrink-0">
                  <s.icon className="h-6 w-6" />
                </div>
                <span className="font-heading font-extrabold text-4xl text-slate-100">0{i + 1}</span>
              </div>
              <h3 className="mt-5 font-heading font-bold text-lg">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Who is it for / Where you can use */}
      <section className="max-w-6xl mx-auto px-5 py-12 md:py-20">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-blue">Who is it for?</span>
          <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl tracking-tight">Where you can use ReachMeNow</h2>
          <p className="mt-3 text-slate-500">One QR code. Many places. Endless peace of mind.</p>
        </div>
        <div className="mt-8 md:mt-12 grid grid-cols-2 md:grid-cols-3 gap-5">
          {useCases.map((u, i) => (
            <motion.div key={u.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.06 }}
              className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid={`usecase-card-${i}`}>
              <div className="aspect-square w-full overflow-hidden bg-slate-100">
                <img src={u.img} alt={u.title} width="800" height="800" loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2.5">
                  <span className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0"><u.icon className="h-5 w-5 text-brand-blue" strokeWidth={2.2} /></span>
                  <h3 className="font-heading font-bold">{u.title}</h3>
                </div>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{u.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Comparison */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-5 py-12 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange">The difference</span>
            <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl tracking-tight">Why not just leave your phone number?</h2>
          </div>
          <div className="mt-8 md:mt-12 overflow-hidden rounded-2xl border border-slate-200 shadow-sm" data-testid="comparison-table">
            <div className="grid grid-cols-[1.2fr_1fr_1fr] bg-slate-50 border-b border-slate-200">
              <div className="p-4 md:p-5 text-xs font-semibold uppercase tracking-wide text-slate-400"></div>
              <div className="p-4 md:p-5 text-sm md:text-base font-heading font-bold text-slate-500 flex items-center gap-2 border-l border-slate-200">Phone Number</div>
              <div className="p-4 md:p-5 text-sm md:text-base font-heading font-bold text-brand-blue flex items-center gap-2 border-l border-slate-200 bg-blue-50/50">ReachMeNow</div>
            </div>
            {comparison.map((row, i) => (
              <div key={row.feature} className={`grid grid-cols-[1.2fr_1fr_1fr] ${i % 2 ? "bg-white" : "bg-slate-50/40"} border-b border-slate-100 last:border-0`} data-testid={`comparison-row-${i}`}>
                <div className="p-4 md:p-5 font-heading font-semibold text-sm md:text-base text-slate-800">{row.feature}</div>
                <div className="p-4 md:p-5 border-l border-slate-100 flex items-start gap-2 text-sm text-slate-500">
                  <X className="h-4 w-4 text-red-400 mt-0.5 shrink-0" /> <span>{row.phone}</span>
                </div>
                <div className="p-4 md:p-5 border-l border-slate-100 flex items-start gap-2 text-sm text-slate-700 bg-blue-50/30">
                  <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" /> <span>{row.rmn}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="max-w-6xl mx-auto px-5 py-12 md:py-20">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-blue">Why ReachMeNow</span>
          <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl tracking-tight">Trusted, private, and simple</h2>
        </div>
        <div className="mt-8 md:mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((b, i) => (
            <motion.div key={b.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
              className="bg-white border border-slate-200 rounded-2xl p-7 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg" data-testid={`benefit-card-${i}`}>
              <div className={`h-12 w-12 rounded-xl ${b.bg} flex items-center justify-center`}>
                <b.icon className={`h-6 w-6 ${b.color}`} strokeWidth={2.2} />
              </div>
              <h3 className="mt-5 font-heading font-bold text-lg">{b.title}</h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-500" data-testid="hero-trust-signals">
          {["No spam", "Unsubscribe anytime", "Privacy first"].map((t) => (
            <span key={t} className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-brand-blue" /> {t}</span>
          ))}
        </div>
      </section>

      {/* Founder Pricing */}
      <section id="pricing" className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-5 py-12 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange">Founder Pricing</span>
            <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl tracking-tight">Back us early, pay less forever</h2>
          </div>
          <div className="mt-8 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {pricing.map((p, i) => (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`relative bg-white border rounded-2xl shadow-sm p-7 flex flex-col ${p.popular ? "border-brand-blue ring-2 ring-brand-blue/20 md:-translate-y-2" : "border-slate-200"}`}
                data-testid={`pricing-card-${i}`}
              >
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-orange text-white text-[11px] font-semibold uppercase tracking-wide px-3 py-1 rounded-full shadow" data-testid="pricing-popular-pill">
                    Most Popular
                  </span>
                )}
                <h3 className="font-heading font-bold text-xl">{p.name}</h3>
                <div className="mt-4">
                  {p.label && <p className="text-xs font-semibold uppercase tracking-wide text-brand-orange">{p.label}</p>}
                  {p.original && <span className="text-slate-400 line-through text-lg">{p.original}</span>}
                  <div className="font-heading font-extrabold text-4xl tracking-tight leading-tight">{p.price}</div>
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-slate-600">
                      <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" /> <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className={`mt-7 h-12 rounded-full text-base w-full ${p.popular ? "bg-brand-blue hover:bg-brand-bluehover text-white" : "bg-brand-orange hover:bg-brand-orangehover text-white"}`}
                  asChild
                  data-testid={`pricing-back-btn-${i}`}
                >
                  <a href={PRICING_URL} target="_blank" rel="noopener noreferrer">{p.name === "Thank You Supporter" ? "Back Us on Indiegogo" : "PreOrder on Indiegogo"}</a>
                </Button>
              </motion.div>
            ))}
          </div>
          <p className="mt-8 text-center text-xs text-slate-400" data-testid="pricing-disclaimer">
            All tiers are fulfilled through our Indiegogo campaign. Physical tags ship from Canada in February 2027. Free shipping to Canada and the United States for all campaign backers. We currently ship to Canada and the United States only. Campaign pricing is available for a limited time only and will not return after the campaign ends.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-5 pb-8">
        <div className="text-center">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-blue">FAQ</span>
          <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl tracking-tight">Questions, answered</h2>
        </div>
        <Accordion type="single" collapsible className="mt-10" value={openFaq} onValueChange={setOpenFaq}>
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border border-slate-200 rounded-2xl mb-3 px-5 bg-white data-[state=open]:shadow-md transition-shadow" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="font-heading font-semibold text-left hover:no-underline text-base">{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-500 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Social proof */}
      <section className="max-w-6xl mx-auto px-5 py-12 md:py-20">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-blue">What People Are Saying</span>
          <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl tracking-tight">The Buzz Before Launch</h2>
        </div>
        <div className="mt-8 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { quote: "I really like the idea behind ReachMeNow. The ability to let someone contact you without exposing your phone number is such a simple but useful concept. I'm looking forward to seeing the product launch.", name: "Ankur", city: "Milton, ON" },
            { quote: "After seeing the ReachMeNow website, I immediately understood the problem it is trying to solve. We put our phone numbers on so many things without thinking about privacy. I'm excited to see the finished product and would definitely consider using it.", name: "Aman", city: "Oakville, ON" },
            { quote: "ReachMeNow is one of those ideas that makes you wonder why something like this wasn't available sooner. The concept is simple, practical, and privacy-focused. I'm eagerly waiting for the launch!", name: "Vaibhav", city: "Brampton, ON" },
          ].map((t, i) => (
            <motion.div key={t.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
              className="bg-white border border-slate-200 rounded-2xl shadow-sm p-7 flex flex-col" data-testid={`testimonial-card-${i}`}>
              <span className="font-heading font-extrabold text-5xl leading-none text-brand-blue" aria-hidden="true">&ldquo;</span>
              <p className="mt-3 text-slate-600 leading-relaxed flex-1">{t.quote}</p>
              <p className="mt-5 text-sm text-slate-400"><span className="font-semibold text-slate-600">{t.name}</span> — {t.city}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Final CTA — Indiegogo */}
      <section className="max-w-6xl mx-auto px-5 py-12 md:py-20">
        <div className="relative overflow-hidden rounded-3xl bg-brand-navy text-white p-10 md:p-16">
          <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-brand-orange/30 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-brand-blue/40 blur-3xl" />
          <div className="relative max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange bg-white/10 px-3 py-1.5 rounded-full">
              <Rocket className="h-3.5 w-3.5" /> Now Live on Indiegogo
            </span>
            <h2 className="mt-6 font-heading font-bold text-3xl md:text-4xl tracking-tight">Become an early supporter</h2>
            <p className="mt-3 text-brand-orange font-semibold" data-testid="final-cta-note">PreOrder now before Early Bird pricing ends.</p>
            <div className="mt-6 space-y-3">
              {[
                { icon: DollarSign, text: "Get exclusive founder pricing" },
                { icon: Users, text: "Help bring privacy-first contact to millions" },
              ].map((p) => (
                <div key={p.text} className="flex items-center gap-3 text-slate-200">
                  <span className="h-8 w-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0"><p.icon className="h-4 w-4 text-brand-orange" /></span>
                  <span className="text-base">{p.text}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button size="lg" className="bg-brand-orange hover:bg-brand-orangehover text-white rounded-full px-7 h-13 text-base shadow-lg transition-all hover:-translate-y-0.5" onClick={goIndiegogo} data-testid="kickstarter-waitlist-btn">
                PreOrder on Indiegogo <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" className="rounded-full px-7 h-13 text-base border-white/30 bg-white/5 text-white hover:bg-white/15" onClick={() => navigate("/demo")} data-testid="final-cta-demo-btn">
                Try the Free Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      <BackToTop />

      {/* Sticky mobile CTA */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white/95 backdrop-blur-xl border-t border-slate-200 p-3" data-testid="mobile-sticky-cta">
        <Button className="w-full h-12 rounded-full bg-brand-orange hover:bg-brand-orangehover text-white text-base" asChild>
          <a href={INDIEGOGO_URL} target="_blank" rel="noopener noreferrer">PreOrder on Indiegogo</a>
        </Button>
      </div>
    </div>
  );
}
